import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import DownloadApp from '@/components/shared/DownloadApp';
import { 
  ShoppingCart,
  Calendar,
  Users,
  Package,
  FileText,
  CreditCard,
  Bell,
  Smartphone,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Shield,
  BarChart3,
  Droplets,
  Sparkles,
  Car,
  DollarSign,
  MessageSquare,
  Gift,
  Tags,
  Percent,
  MapPin,
  UserCheck,
  ClipboardList,
  Camera
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile, faUser, faStickyNote, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

interface SolutionOption {
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted?: boolean;
}

const CarWashSoftwarePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    interest: 'whitelabel',
    subject: '',
    message: ''
  });

  const features: Feature[] = [
    {
      icon: <ShoppingCart className="w-12 h-12" />,
      title: "eCommerce Detailing Store",
      description: "Sell car care products and detailing packages online 24/7",
      benefits: [
        "Online product catalog management",
        "Car wash packages & memberships",
        "Detailing product sales",
        "Gift cards & vouchers",
        "Promotional bundles",
        "Mobile-responsive storefront",
        "Product reviews & ratings",
        "Secure online checkout"
      ]
    },
    {
      icon: <Calendar className="w-12 h-12" />,
      title: "Online Booking & Scheduling",
      description: "Let customers book car wash services 24/7 from any device",
      benefits: [
        "Real-time bay availability",
        "Service duration estimates",
        "Automated booking confirmations",
        "SMS & email reminders",
        "Easy rescheduling options",
        "Multi-service booking",
        "Queue management system",
        "Preferred time slot selection"
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Customer Relationship Management",
      description: "Build loyalty with comprehensive customer data management",
      benefits: [
        "Complete customer profiles",
        "Vehicle & service history tracking",
        "Membership & loyalty programs",
        "Automated birthday offers",
        "Customer preference tracking",
        "Targeted marketing campaigns",
        "Referral program management",
        "Customer feedback collection"
      ]
    },
    {
      icon: <Droplets className="w-12 h-12" />,
      title: "Wash Bay Management",
      description: "Optimize your wash bay operations and workflow",
      benefits: [
        "Real-time bay status tracking",
        "Service assignment & routing",
        "Bay utilization analytics",
        "Multi-location management",
        "Staff scheduling & assignments",
        "Service time tracking",
        "Quality control checklists",
        "Peak hour management"
      ]
    },
    {
      icon: <Package className="w-12 h-12" />,
      title: "Inventory & Product Management",
      description: "Smart inventory control for detailing products and supplies",
      benefits: [
        "Real-time stock tracking",
        "Low stock alerts & auto-reordering",
        "Supplier management",
        "Product usage tracking",
        "Multi-location inventory sync",
        "Barcode scanning integration",
        "Cost tracking & margins",
        "Expiry date management"
      ]
    },
    {
      icon: <Bell className="w-12 h-12" />,
      title: "Real-Time Service Notifications",
      description: "Keep customers informed throughout the wash process",
      benefits: [
        "Push notifications on mobile app",
        "Email & SMS status updates",
        "Service progress tracking",
        "Wash stage updates",
        "Ready for pickup alerts",
        "Payment reminders",
        "Promotional notifications",
        "Service feedback requests"
      ]
    },
    {
      icon: <Gift className="w-12 h-12" />,
      title: "Membership & Loyalty Programs",
      description: "Reward loyal customers and drive recurring revenue",
      benefits: [
        "Unlimited wash memberships",
        "Points-based loyalty system",
        "Tier-based rewards programs",
        "Automatic member discounts",
        "Membership auto-renewal",
        "Referral rewards",
        "Birthday & anniversary specials",
        "Member-exclusive packages"
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Quotes & Invoicing",
      description: "Professional quotations and invoices in seconds",
      benefits: [
        "Instant quote generation",
        "Package-based pricing",
        "Add-on service pricing",
        "Itemized invoicing",
        "Tax calculation & compliance",
        "Discount & coupon management",
        "Multiple payment options",
        "Digital receipt delivery"
      ]
    },
    {
      icon: <CreditCard className="w-12 h-12" />,
      title: "Online Payment Processing",
      description: "Accept payments securely online and on-site",
      benefits: [
        "Credit/Debit card payments",
        "Mobile wallet support",
        "Membership auto-billing",
        "Split payment options",
        "Contactless payments",
        "Payment plan management",
        "Automated receipts",
        "Refund management"
      ]
    },
    {
      icon: <Camera className="w-12 h-12" />,
      title: "Before/After Documentation",
      description: "Visual proof of quality service delivery",
      benefits: [
        "Before & after photo capture",
        "Video documentation",
        "Service quality verification",
        "Customer approval process",
        "Photo galleries per vehicle",
        "Social media integration",
        "Marketing content generation",
        "Insurance claim documentation"
      ]
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: "Analytics & Business Intelligence",
      description: "Data-driven insights to grow your business",
      benefits: [
        "Revenue & profit tracking",
        "Popular service analysis",
        "Customer retention metrics",
        "Bay utilization reports",
        "Staff performance tracking",
        "Membership analytics",
        "Marketing ROI tracking",
        "Custom dashboard creation"
      ]
    },
    {
      icon: <Sparkles className="w-12 h-12" />,
      title: "Service Packages & Add-ons",
      description: "Flexible service packaging and upselling tools",
      benefits: [
        "Customizable wash packages",
        "Add-on service suggestions",
        "Seasonal package promotions",
        "Bundle pricing management",
        "Premium detailing options",
        "Fleet service packages",
        "Subscription-based services",
        "Dynamic pricing rules"
      ]
    }
  ];

  const solutionOptions: SolutionOption[] = [
    {
      title: "Join CarSparesEzyFind",
      description: "List your car wash on our established platform",
      features: [
        "Access to thousands of car owners",
        "Website + Android, iPhone & Huawei apps",
        "Online booking & payment system",
        "Product sales integration",
        "Marketing & SEO support",
        "No setup fees - Start immediately",
        "24/7 technical support",
        "Analytics & sales dashboard"
      ],
      cta: "Join the Marketplace",
      ctaLink: "https://www.CarSparesEzyFind.co.za/list-business"
    },
    {
      title: "WhiteLabel Solution",
      description: "Your own branded car wash management platform",
      features: [
        "Fully branded website & mobile apps",
        "Android, iPhone & Huawei native apps",
        "Complete car wash management system",
        "All features listed above included",
        "Your own domain & branding",
        "48-hour setup guarantee",
        "Unlimited bookings & customers",
        "Dedicated account manager"
      ],
      cta: "Get Custom Quote",
      ctaLink: "#whitelabel-quote",
      highlighted: true
    }
  ];

  const washStages = [
    {
      stage: "Booking Confirmed",
      icon: <CheckCircle className="w-6 h-6" />,
      notification: "Booking confirmation with service details & estimated time"
    },
    {
      stage: "Reminder Sent",
      icon: <Bell className="w-6 h-6" />,
      notification: "SMS reminder 2 hours before appointment"
    },
    {
      stage: "Vehicle Checked In",
      icon: <Car className="w-6 h-6" />,
      notification: "Push notification: 'Your vehicle has been checked in - Bay #3'"
    },
    {
      stage: "Pre-Wash Inspection",
      icon: <Camera className="w-6 h-6" />,
      notification: "Before photos sent via email for your records"
    },
    {
      stage: "Washing in Progress",
      icon: <Droplets className="w-6 h-6" />,
      notification: "Real-time update: 'Exterior wash in progress - 15 mins remaining'"
    },
    {
      stage: "Interior Detailing",
      icon: <Sparkles className="w-6 h-6" />,
      notification: "Status update: 'Interior cleaning & detailing underway'"
    },
    {
      stage: "Final Quality Check",
      icon: <UserCheck className="w-6 h-6" />,
      notification: "Final inspection complete - After photos sent"
    },
    {
      stage: "Ready for Pickup",
      icon: <Bell className="w-6 h-6" />,
      notification: "Push notification & SMS: 'Your vehicle is sparkling clean and ready!'"
    },
    {
      stage: "Payment Complete",
      icon: <CreditCard className="w-6 h-6" />,
      notification: "Digital invoice and loyalty points added to your account"
    }
  ];

  const stats = [
    { value: "3,000+", label: "Car Wash Locations" },
    { value: "500K+", label: "Monthly Washes" },
    { value: "99%", label: "Customer Satisfaction" },
    { value: "48hrs", label: "Setup Time" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours with your custom quote.');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Head>
        <title>Car Wash Software - Online Booking, eCommerce & Management | CarSparesEzyFind</title>
        <meta name="description" content="Complete car wash management software with online booking, eCommerce detailing store, CRM, inventory management, customer notifications, and mobile apps for Android, iPhone & Huawei." />
        <meta name="keywords" content="car wash software, car detailing software, car wash management system, online booking, car wash app, detailing products ecommerce" />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-cyan-600 via-blue-600 to-blue-700 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Droplets className="w-4 h-4 mr-2 text-cyan-300" />
                <span className="text-sm font-semibold">Complete Car Wash & Detailing Solution</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Revolutionize Your <span className="text-cyan-300">Car Wash Business</span>
              </h1>
              
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Online Booking • eCommerce Store • CRM • Memberships • Notifications • Payments
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="https://www.CarSparesEzyFind.co.za/list-business" 
                  className="bg-cyan-400 hover:bg-cyan-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center"
                >
                  Join Our Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="#whitelabel-quote" 
                  className="bg-white hover:bg-gray-100 text-blue-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center border-2 border-white"
                >
                  Get Your Own Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-cyan-300">{stat.value}</div>
                    <div className="text-sm text-blue-100 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Apps & eCommerce Highlight */}
        <section className="py-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
              <div className="flex items-center gap-4">
                <ShoppingCart className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Online Detailing Store</h3>
                  <p className="text-purple-100">Sell products & packages 24/7</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-16 bg-white/30"></div>
              <div className="flex items-center gap-4">
                <Smartphone className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Mobile Apps</h3>
                  <p className="text-purple-100">Android, iPhone & Huawei</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Everything Your Car Wash Needs
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive software solution designed for modern car wash and detailing businesses
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-8 hover:shadow-2xl transition-all hover:border-cyan-500 transform hover:-translate-y-2"
                >
                  <div className="text-cyan-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership & Loyalty Highlight */}
        <section className="py-20 bg-gradient-to-br from-purple-900 to-pink-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <Gift className="w-16 h-16 text-yellow-300 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Membership & Loyalty Programs
                </h2>
                <p className="text-xl text-purple-200">
                  Build recurring revenue with subscription-based memberships
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <Gift className="w-8 h-8" />, title: "Unlimited Plans", desc: "Monthly unlimited wash subscriptions" },
                  { icon: <Star className="w-8 h-8" />, title: "Points System", desc: "Earn & redeem loyalty points" },
                  { icon: <Percent className="w-8 h-8" />, title: "Member Discounts", desc: "Automatic member pricing" },
                  { icon: <Users className="w-8 h-8" />, title: "Referral Rewards", desc: "Refer friends & earn credits" },
                  { icon: <Tags className="w-8 h-8" />, title: "Tier Levels", desc: "Bronze, Silver, Gold tiers" },
                  { icon: <Calendar className="w-8 h-8" />, title: "Auto Renewal", desc: "Hassle-free membership billing" },
                  { icon: <Bell className="w-8 h-8" />, title: "Birthday Offers", desc: "Special birthday discounts" },
                  { icon: <DollarSign className="w-8 h-8" />, title: "Package Deals", desc: "Member-exclusive bundles" }
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                    <div className="text-yellow-300 flex justify-center mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                    <p className="text-purple-200 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Customer Journey Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Bell className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Real-Time Customer Updates
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Keep customers informed at every stage of the wash process
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-500 to-blue-500 hidden md:block"></div>

                {washStages.map((stage, index) => (
                  <div key={index} className="relative mb-8 md:ml-20">
                    <div className="absolute -left-20 top-2 hidden md:block">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white shadow-lg">
                        {stage.icon}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-cyan-500 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="md:hidden text-cyan-600">{stage.icon}</div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{stage.stage}</h4>
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600">{stage.notification}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* eCommerce Features Highlight */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <ShoppingCart className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Sell Detailing Products Online
                </h2>
                <p className="text-xl text-gray-600">
                  Turn your car wash into a profitable eCommerce business
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { icon: <Sparkles className="w-8 h-8" />, title: "Car Care Products", desc: "Waxes, polishes, cleaning supplies" },
                  { icon: <Package className="w-8 h-8" />, title: "Detailing Kits", desc: "Pre-packaged product bundles" },
                  { icon: <Gift className="w-8 h-8" />, title: "Gift Cards", desc: "Digital gift cards & vouchers" },
                  { icon: <Calendar className="w-8 h-8" />, title: "Service Packages", desc: "Wash packages & memberships" },
                  { icon: <Tags className="w-8 h-8" />, title: "Promotions", desc: "Seasonal offers & discounts" },
                  { icon: <Star className="w-8 h-8" />, title: "Reviews", desc: "Customer product reviews" }
                ].map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border-2 border-cyan-200 hover:shadow-lg transition-all">
                    <div className="text-cyan-600 mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-2 text-gray-900">{item.title}</h4>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Solution Options Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Solution
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join our established platform or get your own branded solution
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {solutionOptions.map((option, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    option.highlighted 
                      ? 'bg-gradient-to-br from-cyan-600 to-blue-700 text-white shadow-2xl transform scale-105' 
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {option.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        RECOMMENDED
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-3xl font-bold mb-2 ${option.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {option.title}
                    </h3>
                    <p className={option.highlighted ? 'text-cyan-100' : 'text-gray-600'}>
                      {option.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          option.highlighted ? 'text-cyan-300' : 'text-green-500'
                        }`} />
                        <span className={option.highlighted ? 'text-cyan-50' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={option.ctaLink}
                    className={`block text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                      option.highlighted
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900'
                        : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                    }`}
                  >
                    {option.cta}
                    <ArrowRight className="inline-block ml-2 w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WhiteLabel Quote Section */}
        <section id="whitelabel-quote" className="py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Zap className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Get Your WhiteLabel Car Wash Platform
                </h2>
                <p className="text-xl text-cyan-100">
                  Custom branded website & mobile apps - Ready in 48 hours!
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Fully branded website with your logo",
                      "Android, iPhone & Huawei mobile apps",
                      "Complete eCommerce detailing store",
                      "Online booking & scheduling system",
                      "CRM & customer management",
                      "Membership & loyalty programs",
                      "Inventory & product management",
                      "Invoicing & payment processing",
                      "Push notifications & email alerts",
                      "Before/after photo documentation",
                      "Analytics & reporting dashboard",
                      "Your own domain name",
                      "Unlimited bookings & customers",
                      "Multi-location support"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Clock className="w-6 h-6 text-blue-400 mr-2" />
                    Quick Setup Process
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-cyan-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</div>
                      <div>
                        <h4 className="font-bold mb-1">Submit Your Details</h4>
                        <p className="text-cyan-100 text-sm">Fill out the form below with your business information</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-cyan-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</div>
                      <div>
                        <h4 className="font-bold mb-1">Receive Custom Quote</h4>
                        <p className="text-cyan-100 text-sm">Get a detailed proposal within 24 hours</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-cyan-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</div>
                      <div>
                        <h4 className="font-bold mb-1">Launch Your Platform</h4>
                        <p className="text-cyan-100 text-sm">Your branded solution goes live in 48 hours</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-400/20 border-2 border-yellow-400 rounded-lg">
                    <p className="font-bold text-yellow-300">
                      ⚡ Special Offer: First 10 car washes get 35% off!
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
                <h3 className="text-3xl font-bold text-center mb-8">Request Your Custom Quote</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='name' 
                        placeholder='Enter name' 
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-cyan-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faUser} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='flex flex-col md:flex-row md:gap-5'>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Phone *</label>
                      <div className='relative'>
                        <div className='flex'>
                          <input 
                            type='text' 
                            name='code' 
                            placeholder='27' 
                            value={'+27'} 
                            disabled 
                            className='form-control border border-gray-300 border-r-0 text-sm xl:text-[14px] 2xl:text-md h-10 px-3 w-[50px] rounded-l' 
                          />
                          <input 
                            type='text' 
                            name='phone' 
                            placeholder='Enter mobile number' 
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded-r focus:border-cyan-500 focus:outline-none' 
                          />
                        </div>
                        <FontAwesomeIcon icon={faMobile} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Email *</label>
                      <div className='relative'>
                        <input 
                          type='email' 
                          name='email' 
                          placeholder='Enter email Id' 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-cyan-500 focus:outline-none' 
                        />
                        <FontAwesomeIcon icon={faEnvelope} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Car Wash Business Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='businessName' 
                        placeholder='Enter your car wash name' 
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-cyan-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faBuilding} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>I'm interested in *</label>
                    <select
                      name='interest'
                      value={formData.interest}
                      onChange={handleChange}
                      className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-cyan-500 focus:outline-none'
                    >
                      <option value="whitelabel">WhiteLabel Solution</option>
                      <option value="join">Joining CarSparesEzyFind</option>
                      <option value="both">Both Options - Need More Info</option>
                    </select>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Subject</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='subject' 
                        placeholder='Subject' 
                        value={formData.subject}
                        onChange={handleChange}
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-cyan-500 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faStickyNote} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Message</label>
                    <textarea 
                      name='message'
                      placeholder='Tell us about your car wash business and requirements' 
                      rows={5} 
                      value={formData.message}
                      onChange={handleChange}
                      className='form-control py-3 border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full px-3 rounded focus:border-cyan-500 focus:outline-none'
                    />
                  </div>

                  <div className='text-right'>
                    <button 
                      type="submit"
                      className='bg-cyan-600 text-white border border-cyan-600 uppercase transition-all hover:bg-white hover:text-cyan-600 px-10 py-3 rounded font-bold'
                    >
                      Get My Quote
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  🔒 Your information is secure. We'll contact you within 24 hours with a detailed quote.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Car Wash Business?
            </h2>
            <p className="text-xl text-cyan-100 mb-8 max-w-3xl mx-auto">
              Join thousands of successful car washes using our platform to increase revenue and customer loyalty
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://www.CarSparesEzyFind.co.za/list-business"
                className="bg-white hover:bg-gray-100 text-blue-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Join CarSparesEzyFind
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="#whitelabel-quote"
                className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Get WhiteLabel Solution
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
      <DownloadApp/>
    </>
  );
};

export default CarWashSoftwarePage;